import {
  Button,
  CircularProgress,
  Grid,
  Tab,
  Tabs,
  TextField,
} from "@material-ui/core";
import { postRequest } from "api/apiCaller";
import Collapse from "components/collapse";
import { useFormik } from "formik";
import { useSnackbar } from "notistack";
import Order from "pages/agent-chat-ticket/order";
import TicketDetail from "pages/agent-chat-ticket/ticket/TicketDetail";
import TicketHistory from "pages/agent-chat-ticket/ticket/TicketHistory";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getTicketType } from "redux/miniCrm/ticket";
import { getListChatTicketHistoryByUsername } from "redux/ringme-ott-sdk";
import * as Yup from "yup";

import mainClasses from "../AgentChatTicket.module.css";
import SendingSMS from "./SendingSMS";

const Ticket = () => {
  const [tabTicket, setTabTicket] = useState(0);

  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const listTicketTypes = useSelector((state) => state.ticket.ticketType);
  const threadInfo = useSelector((state) => state.ringmeOttSdk.threadInfo);
  const isCustomer = !threadInfo?.sender?.department; // không có department từ gotech thì là khách hàng, còn không là nhân viên nội bộ

  const [isCanEdit, setIsCanEdit] = useState(false);
  const customerNameInputRef = useRef();
  const [customerInfo, setCustomerInfo] = useState({
    customerName: "",
    customerPhone: "",
    customerAddress: "",
    customerEmail: "",
  });

  const validationSchemaCustomerInfo = Yup.object().shape({
    customerName: Yup.string().required("Không được trống"),
    customerPhone: Yup.string().required("Không được trống"),
    customerEmail: Yup.string(),
    customerAddress: Yup.string(),
  });

  const validationSchemaTicket = Yup.lazy((values) => {
    return Yup.object().shape({
      service: Yup.object().nullable(),
      product: Yup.string(),
      ...(!isTicketType("tư vấn dịch vụ") && {
        packageCode: Yup.string().required(
          "Mã tin thu gom / Mã vận đơn không được trống"
        ),
      }),
      type: Yup.string().required("Loại yêu cầu không được trống"),
      ...(!!getTicketTypes(values.type).length && {
        ticketDetail: Yup.string().required(
          "Nội dung yêu cầu không được trống"
        ),
      }),
      ...(isTicketType("tư vấn dịch vụ") && {
        transferUnit: Yup.object().shape({
          code: Yup.string().required("Không được trống"),
        }),
      }),
      description: Yup.string().required("Chi tiết yêu cầu không được trống"),

      ...(values.moreMisson && {
        task: Yup.array().of(
          Yup.object().shape({
            description: Yup.string().required(
              "Chi tiết nhiệm vụ không được trống"
            ),
            type: Yup.string().required("Loại nhiệm vụ không được trống"),
            actionDate: Yup.string().required(
              "Thời gian thực hiện không được trống"
            ),
          })
        ),
        customFields: Yup.object().shape({
          conversationId: Yup.string(), // TODO required
          conversationName: Yup.string(),
        }),
      }),
    });
  });

  const handleSubmitCustomerInfo = (values, { setSubmitting }) => {
    const customerSubmitValue = {
      customerName: values.customerName?.trim() || "",
      customerPhone: values.customerPhone?.trim() || "",
      customerAddress: values.customerAddress?.trim() || "",
      customerEmail: values.customerEmail?.trim() || "",
    };

    postRequest(
      `/mini-crm/vnpost-crm-gateway/post-contact-crm-vnpost`,
      customerSubmitValue
    )
      .then((res) => {
        if (res.data) {
          enqueueSnackbar("Lưu thông tin khách hàng thành công", {
            variant: "success",
          });
          setCustomerInfo(customerSubmitValue);
          formCustomerInfo.setValues(customerSubmitValue);
          setIsCanEdit(false);
        } else {
          enqueueSnackbar("Lưu thông tin khách hàng thất bại!", {
            variant: "error",
          });
        }
        setSubmitting(false);
      })
      .catch((err) => {
        enqueueSnackbar(
          `Lưu thông tin khách hàng thất bại! ${
            err.response?.data?.error?.message || err.message || ""
          }`,
          {
            variant: "error",
          }
        );
        setSubmitting(false);
      });
  };

  const handleSubmitTicket = async (values, { setSubmitting }) => {
    if (!threadInfo?.threadId) {
      enqueueSnackbar("Chưa chọn cuộc chat!", {
        variant: "warning",
      });
      return;
    }

    if (threadInfo?.action === "End") {
      enqueueSnackbar("Chỉ có thể tạo ticket cho cuộc chat chưa kết thúc", {
        variant: "warning",
      });
      return;
    }

    if (!isCustomer) {
      enqueueSnackbar("Không thể tạo ticket cho tài khoản nhân viên!", {
        variant: "warning",
      });
      return;
    }

    try {
      const res = await postRequest(`/mini-crm/ticket`, {
        type: values.type,
        phone: customerInfo?.customerPhone || "",
        tasks: values.task,
        customFields: {
          conversationId: threadInfo.threadId || "",
          conversationName: values.customFields?.conversationName || "",
        },
        source: "ott",
        channel: threadInfo.appId || "",
        attributes: {
          service: values.service?.key || "",
          product: values.product || "",
          packageCode: values.packageCode?.trim() || "",
          description: values.description?.trim() || "",
          ticketDetail: values.ticketDetail || "",
          ...(isTicketType("tư vấn dịch vụ") && {
            transferUnit: {
              code: values.transferUnit?.code || "",
              name: values.transferUnit?.name || "",
            },
            businessProduct: values.businessProduct?.trim() || "",
            outputPerMonth: values.outputPerMonth?.toString() || "",
          }),
          customerName: customerInfo?.customerName || "",
          customerPhone: customerInfo?.customerPhone || "",
          customerAddress: customerInfo?.customerAddress || "",
          customerEmail: customerInfo?.customerEmail || "",
          customerUsername: threadInfo.sender?.username || "",
        },
      });
      if (res.data) {
        formTicket.resetForm();
        enqueueSnackbar("Lưu thông tin ticket thành công", {
          variant: "success",
        });
        if (threadInfo?.sender?.phoneNumber)
          dispatch(
            getListChatTicketHistoryByUsername(threadInfo.sender.phoneNumber)
          );
      } else {
        enqueueSnackbar("Lưu thông tin ticket thất bại!", {
          variant: "error",
        });
      }
    } catch (err) {
      enqueueSnackbar(
        `Lưu thông tin ticket thất bại! ${
          err.response?.data?.error?.message || err.message || ""
        }`,
        {
          variant: "error",
        }
      );
    }
    setSubmitting(false);
  };

  const formCustomerInfo = useFormik({
    initialValues: {
      customerName: "",
      customerPhone: "",
      customerAddress: "",
      customerEmail: "",
    },
    onSubmit: handleSubmitCustomerInfo,
    validationSchema: validationSchemaCustomerInfo,
  });

  const formTicket = useFormik({
    initialValues: {
      packageCode: "",
      service: null,
      product: "",
      type: "",
      ticketDetail: "",
      description: "",
      transferUnit: { code: "", name: "" },
      businessProduct: "",
      outputPerMonth: "",

      moreMisson: false,
      task: [],
      customFields: {
        conversationId: "",
        conversationName: "",
      },
    },
    onSubmit: handleSubmitTicket,
    validationSchema: validationSchemaTicket,
    // validateOnChange: false,
    // validateOnBlur: true,
  });

  //  lấy luôn thông tin người dùng trong threadInfo
  const getInfoCustomer = useCallback(async () => {
    const phone = threadInfo?.sender?.phoneNumber || "";

    formCustomerInfo.resetForm();
    formTicket.resetForm();
    setIsCanEdit(false);

    if (phone) {
      const customerInfoData = {
        customerName: threadInfo?.sender?.fullname || "",
        customerPhone: phone,
        customerAddress: threadInfo?.sender?.address || "",
        customerEmail: threadInfo?.sender?.email || "",
      };
      setCustomerInfo(customerInfoData);
      Object.entries(customerInfoData).forEach(([key, value]) =>
        formCustomerInfo.setFieldValue(key, value)
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadInfo]);

  const isTicketType = (ticketTypeName) =>
    !!listTicketTypes &&
    listTicketTypes
      .find((item) => item.id === formTicket.values.type)
      ?.name?.toLowerCase()
      .includes(ticketTypeName);

  const getTicketTypes = (ticketTypeId) => {
    if (ticketTypeId) {
      return (
        listTicketTypes.find((item) => item.id === ticketTypeId)?.ticketTypes ||
        []
      );
    }
    return [];
  };

  useEffect(() => {
    // khi phone tab thay đổi
    getInfoCustomer();
  }, [getInfoCustomer]);

  useEffect(() => {
    dispatch(getTicketType());
  }, [dispatch]);

  return (
    <>
      <Tabs
        value={tabTicket}
        onChange={(_e, value) => setTabTicket(value)}
        TabIndicatorProps={{ style: { background: "#FCB71E" } }}>
        {[
          { id: 0, name: "Thông tin tác vụ", disabled: false },
          { id: 1, name: "Đơn hàng", disabled: !isCustomer },
          { id: 2, name: "Lịch sử tương tác", disabled: !isCustomer },
        ].map((tab) => (
          <Tab
            label={tab.name}
            value={tab.id}
            style={{
              color: tab.id === tabTicket ? "#FCB71E" : "#263238",
            }}
            disabled={tab.disabled}
            classes={{
              root: mainClasses.tab,
              labelContainer: mainClasses.tabLabel,
            }}
            key={tab.id}
          />
        ))}
      </Tabs>
      <div
        style={{
          display: tabTicket === 0 ? "block" : "none",
        }}>
        <div style={{ marginBottom: 10 }}>
          <Collapse
            title={(openCustomerInfo) => (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}>
                <span className={mainClasses.title}>
                  {isCustomer ? "Thông tin khách hàng" : "Thông tin nhân viên"}
                </span>
                {!isCanEdit && openCustomerInfo && isCustomer && (
                  <i
                    onClick={() => {
                      setIsCanEdit(true);
                      customerNameInputRef.current.focus();
                    }}
                    className={`fas fa-edit ${mainClasses.icon}`}
                    style={{
                      fontSize: 20,
                      marginRight: 12,
                      cursor: "pointer",
                    }}></i>
                )}
              </div>
            )}
            titleIcon={
              <i className={`fas fa-user-circle ${mainClasses.iconTitle}`} />
            }
            defaultOpen>
            <form onSubmit={formCustomerInfo.handleSubmit}>
              <Grid container>
                <Grid
                  item
                  xs={12}
                  md={6}
                  style={{ display: "flex", marginBottom: 5 }}>
                  <span className={mainClasses.iconInput}>
                    <i className="fas fa-id-card"></i>
                  </span>
                  <TextField
                    variant="standard"
                    fullWidth
                    inputRef={customerNameInputRef}
                    placeholder="Họ và tên *"
                    InputProps={{
                      spellCheck: "false",
                      readOnly: !isCanEdit,
                      classes: { input: mainClasses.input },
                    }}
                    style={{ marginRight: 10 }}
                    {...formCustomerInfo.getFieldProps("customerName")}
                    error={
                      formCustomerInfo.touched.customerName &&
                      Boolean(formCustomerInfo.errors.customerName)
                    }
                    helperText={
                      formCustomerInfo.touched.customerName &&
                      formCustomerInfo.errors.customerName
                    }
                  />
                </Grid>

                <Grid
                  item
                  xs={12}
                  md={6}
                  style={{ display: "flex", marginBottom: 5 }}>
                  <span className={mainClasses.iconInput}>
                    <i className="fas fa-phone-alt"></i>
                  </span>
                  <TextField
                    variant="standard"
                    placeholder="Số điện thoại *"
                    fullWidth
                    InputProps={{
                      spellCheck: "false",
                      readOnly: true,
                      type: "tel",
                      classes: { input: mainClasses.input },
                    }}
                    style={{ marginRight: 10 }}
                    {...{
                      ...formCustomerInfo.getFieldProps("customerPhone"),
                      onChange: (e) => {
                        let phoneInputValue = e.target.value;
                        let maxPhoneLength = 10;
                        if (phoneInputValue.slice(0, 3) === "+84") {
                          maxPhoneLength = 12;
                        }
                        if (phoneInputValue.slice(0, 2) === "84") {
                          maxPhoneLength = 11;
                        }
                        if (phoneInputValue[0] === "0") {
                          maxPhoneLength = 10;
                        }
                        if (phoneInputValue.length <= maxPhoneLength) {
                          phoneInputValue = phoneInputValue.replace(
                            /[^0-9+]/g,
                            ""
                          );
                          formCustomerInfo.setFieldValue(
                            "customerPhone",
                            phoneInputValue
                          );
                        }
                      },
                    }}
                    required
                    error={
                      formCustomerInfo.touched.customerPhone &&
                      Boolean(formCustomerInfo.errors.customerPhone)
                    }
                    helperText={
                      formCustomerInfo.touched.customerPhone &&
                      formCustomerInfo.errors.customerPhone
                    }
                  />
                </Grid>

                <Grid item xs={12} md={6} style={{ display: "flex" }}>
                  <span className={mainClasses.iconInput}>
                    <i className="fas fa-envelope"></i>
                  </span>
                  <TextField
                    variant="standard"
                    placeholder="Email"
                    fullWidth
                    InputProps={{
                      spellCheck: "false",
                      readOnly: !!customerInfo.customerEmail || !isCanEdit,
                      classes: { input: mainClasses.input },
                    }}
                    style={{ marginRight: 10 }}
                    {...formCustomerInfo.getFieldProps("customerEmail")}
                    error={
                      formCustomerInfo.touched.customerEmail &&
                      Boolean(formCustomerInfo.errors.customerEmail)
                    }
                    helperText={
                      formCustomerInfo.touched.customerEmail &&
                      formCustomerInfo.errors.customerEmail
                    }
                  />
                </Grid>
                <Grid item xs={12} md={6} style={{ display: "flex" }}>
                  <span className={mainClasses.iconInput}>
                    <i className="fas fa-map-marker-alt"></i>
                  </span>
                  <TextField
                    variant="standard"
                    placeholder="Địa chỉ"
                    fullWidth
                    style={{ marginRight: 10 }}
                    InputProps={{
                      spellCheck: "false",
                      readOnly: !isCanEdit,
                      classes: { input: mainClasses.input },
                    }}
                    {...formCustomerInfo.getFieldProps("customerAddress")}
                  />
                </Grid>
              </Grid>
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  marginTop: 10,
                }}>
                {isCanEdit && (
                  <>
                    <Button
                      variant="contained"
                      color="primary"
                      type="submit"
                      disabled={
                        formCustomerInfo.isSubmitting ||
                        !formCustomerInfo.isValid
                      }
                      className={mainClasses.button}
                      style={{
                        marginRight: 12,
                      }}>
                      Đồng ý
                      {formCustomerInfo.isSubmitting && (
                        <CircularProgress
                          style={{
                            width: 16,
                            height: 16,
                            marginLeft: 5,
                            color: "white",
                          }}
                        />
                      )}
                    </Button>
                    <Button
                      onClick={() => {
                        formCustomerInfo.resetForm();
                        formCustomerInfo.setFieldValue(
                          "customerPhone",
                          customerInfo.customerPhone
                        );
                        formCustomerInfo.setFieldValue(
                          "customerEmail",
                          customerInfo.customerEmail
                        );
                        formCustomerInfo.setFieldValue(
                          "customerName",
                          customerInfo.customerName
                        );
                        formCustomerInfo.setFieldValue(
                          "customerAddress",
                          customerInfo.customerAddress
                        );
                        setIsCanEdit(false);
                      }}
                      className={mainClasses.button}
                      style={{
                        textTransform: "none",
                        marginRight: 10,
                      }}>
                      Hủy bỏ
                    </Button>
                  </>
                )}
              </div>
            </form>
          </Collapse>
        </div>
        <Collapse
          title={<span className={mainClasses.title}>Chi tiết ticket</span>}
          titleIcon={
            <span className={mainClasses.iconTitle}>
              <i
                className={`fas fa-ticket-alt`}
                style={{
                  transform: "rotate(-45deg)",
                }}
              />
            </span>
          }
          defaultOpen>
          <TicketDetail
            formTicket={formTicket}
            getTicketTypes={getTicketTypes}
            isCanEdit={isCanEdit}
            isTicketType={isTicketType}
            listTicketTypes={listTicketTypes}
          />
        </Collapse>

        <div style={{ marginTop: 10 }}>
          <SendingSMS />
        </div>
      </div>
      <div
        style={{
          display: tabTicket === 1 ? "block" : "none",
        }}>
        <Order />
      </div>
      <div
        style={{
          display: tabTicket === 2 ? "block" : "none",
        }}>
        <TicketHistory />
      </div>
    </>
  );
};

export const RequiredField = () => <span style={{ color: "red" }}>(*)</span>;

export default Ticket;
